import { db } from "@lyder-talk/core/lib/db-client";
import type { Notification } from "@/hooks/use-notifications";
import { auth } from "@lyder-talk/core/lib/auth-client";

/**
 * Safely extract study_group_id from notification metadata
 */
function getStudyGroupId(notification: Notification): string | undefined {
  if (notification.metadata && typeof notification.metadata === 'object' && 'study_group_id' in notification.metadata) {
    return notification.metadata.study_group_id as string | undefined;
  }
  return undefined;
}

/**
 * Generate a link for a notification based on its type and data
 * Links are generated in code instead of being stored in the database
 */
export async function getNotificationLink(
  notification: Notification
): Promise<string | null> {
  const { notification_type } = notification;
  const study_group_id = getStudyGroupId(notification);

  switch (notification_type) {
    case "session_shared":
      // For session_shared, get the session slug from activity_data
      // Match activity by group, type, and approximate timestamp
      if (!study_group_id) {
        return null;
      }
      try {
        const notificationTime = new Date(notification.created_at);
        const timeWindowStart = new Date(notificationTime.getTime() - 60000); // 1 minute before
        const timeWindowEnd = new Date(notificationTime.getTime() + 60000); // 1 minute after

        const { data: activity } = await db.from("study_group_activities")
          .select("activity_data")
          .eq("study_group_id", study_group_id)
          .eq("activity_type", "session_shared")
          .gte("created_at", timeWindowStart.toISOString())
          .lte("created_at", timeWindowEnd.toISOString())
          .order("created_at", { ascending: false })
          .limit(1)
          .single();

        if (activity?.activity_data) {
          const activityData = activity.activity_data as {
            session_slug?: string;
            practice_session_id?: string;
          };

          if (activityData.session_slug) {
            return `/session/${activityData.session_slug}`;
          }

          // Fallback: try to get slug from practice_session_id
          if (activityData.practice_session_id) {
            const { data: session } = await db.from("practice_sessions")
              .select("slug")
              .eq("id", activityData.practice_session_id)
              .single();

            if (session?.slug) {
              return `/session/${session.slug}`;
            }
          }
        }
      } catch (error) {
        console.error("Error fetching session slug for notification:", error);
      }
      // Fallback to group page if we can't get session slug
      return `/study-groups/${study_group_id}`;

    case "session_deleted":
      return study_group_id ? `/study-groups/${study_group_id}` : null;

    case "member_joined":
      return study_group_id ? `/study-groups/${study_group_id}` : null;

    case "member_left":
      // For the member who left, go to study groups list
      // For admins, go to the specific group
      return study_group_id ? `/study-groups/${study_group_id}` : null;

    case "invitation":
      // For invitations, we need to get the invite_token
      // Fetch the most recent invitation for this user and group
      if (!study_group_id) {
        return null;
      }
      try {
        const { data: { user } } = await auth.getUser();
        if (user) {
          const { data: invitation } = await db.from("study_group_invitations")
            .select("invite_token")
            .eq("study_group_id", study_group_id)
            .eq("user_id", user.id)
            .eq("status", "pending")
            .order("created_at", { ascending: false })
            .limit(1)
            .single();

          if (invitation?.invite_token) {
            return `/invite/${invitation.invite_token}`;
          }
        }
      } catch (error) {
        console.error("Error fetching invite token for notification:", error);
      }
      // Fallback to group page
      return `/study-groups/${study_group_id}`;

    case "mention":
    case "challenge":
    case "announcement":
      // Default to group page for other types
      return study_group_id ? `/study-groups/${study_group_id}` : null;

    case "session_comment":
    case "session_comment_reply":
      try {
        const metadata = notification.metadata as {
          session_slug?: string;
          session_id?: string;
        } | null;

        if (metadata?.session_slug) {
          return `/session/${metadata.session_slug}`;
        }

        if (metadata?.session_id) {
          const { data: session } = await db.from("practice_sessions")
            .select("slug")
            .eq("id", metadata.session_id)
            .single();

          if (session?.slug) {
            return `/session/${session.slug}`;
          }
        }
      } catch (error) {
        console.error("Error fetching session slug for comment notification:", error);
      }
      return "/my-topics";

    case "vocabulary_review_reminder":
      // Vocabulary review reminder - go to MyVocabulary page with due filter
      return `/vocabulary?filter=due`;

    default:
      return study_group_id ? `/study-groups/${study_group_id}` : null;
  }
}

